/**
 * Module dependencies.
 */

import {
  Button,
  Checkbox,
  CheckboxGroup,
  HelpText,
  Input,
  Message,
  Select
} from 'src/components/core';

import { FormDataProps, subjectValues } from 'src/types/forms';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { regexes } from 'src/core/utils/regexes';
import { submitContactForm } from 'src/api/form/submit-contact-form';
import { switchProp, theme } from 'styled-tools';
import { units } from '@untile/react-components';
import { useAuth } from 'src/context/auth/context';
import { useCountriesList } from 'src/hooks/use-countries-list';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import TermsConditionsCheckboxLabel from 'src/components/core/forms/terms-conditions-checkbox-label';
import Transition from 'react-transition-group/Transition';
import map from 'lodash/map';
import omit from 'lodash/omit';
import styled, { css } from 'styled-components';
import uniqBy from 'lodash/uniqBy';
import useTechnologies from 'src/api/technologies/use-technologies';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `Form` styled component.
 */

const Form = styled.form`
  position: relative;
`;

/**
 * `StyledCheckbox` styled component.
 */

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: ${units(5)};
`;

/**
 * `StyledCheckboxGroup` styled component.
 */

const StyledCheckboxGroup = styled(CheckboxGroup)`
  margin-bottom: ${units(4)};
`;

/**
 * `MessageWrapper` styled component.
 */

const MessageWrapper = styled.div<{ transitionState: any }>`
  padding-top: ${units(6)};
  transition: opacity ${theme('animations.defaultTransition')};

  ${switchProp('transitionState', {
    entered: css`
      opacity: 1;
    `,
    entering: css`
      opacity: 1;
    `,
    exited: css`
      opacity: 0;
    `,
    exiting: css`
      opacity: 0;
    `
  })}
`;

/**
 * `ContactForm` component.
 */

const ContactForm = ({ className }: Props): ReactElement => {
  const { isAuthenticated, user } = useAuth();
  const { locale, translate } = useTranslate();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch
  } = useForm({
    mode: 'onBlur',
    ...(isAuthenticated && {
      defaultValues: {
        acceptTerms: null,
        company: user.info.company,
        country: null,
        email: user.email,
        // eslint-disable-next-line id-match
        gas_type: null,
        message: null,
        name: user.info.name,
        phone: user.info?.phone ?? null,
        position: user.info?.position ?? null,
        subject: null,
        // eslint-disable-next-line id-match
        subscribe_to_newsletter: null,
        // eslint-disable-next-line id-match
        technology_type: null
      }
    })
  });

  const [isMessageVisible, setIsMessageVisible] = useState<boolean>();
  const [messageAppearance, setMessageAppearance] = useState<
    'error' | 'success'
  >('success');

  const { isLoading: isSubmitting, mutateAsync } =
    useMutation(submitContactForm);

  const countriesList = useCountriesList();
  const isPartnership = watch('subject') === subjectValues.partnerships;
  const { data: technologies, isLoading: isLoadingTechnologies } =
    useTechnologies({
      enabled: isPartnership,
      filterParams: {}
    });

  const technologiesOptions = useMemo(
    () =>
      map(uniqBy(technologies, 'title'), ({ title }) => ({
        label: title,
        value: title
      })),
    [technologies]
  );

  const { executeRecaptcha } = useGoogleReCaptcha();
  const onSubmit = useCallback(
    async (data: FormDataProps) => {
      const recaptchaToken = await executeRecaptcha('contact').then(
        token => token
      );

      const formData = {
        ...omit(data, 'acceptTerms'),
        // eslint-disable-next-line id-match
        recaptcha_token: recaptchaToken,
        ...(data.subject !== subjectValues.partnerships && {
          // eslint-disable-next-line id-match
          gas_type: null,
          // eslint-disable-next-line id-match
          technology_type: null
        })
      };

      return mutateAsync(
        { formData, locale },
        {
          onError: () => setMessageAppearance('error'),
          onSettled: () => {
            setIsMessageVisible(true);

            setTimeout(() => {
              setIsMessageVisible(false);
            }, 8000);
          },
          onSuccess: () => {
            reset();
          }
        }
      );
    },
    [executeRecaptcha, locale, mutateAsync, reset]
  );

  const animProps = {
    'data-scroll': 'fade-in-up-small',
    'data-scroll-offset': '20%'
  };

  return (
    <Form
      {...animProps}
      className={className}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        disabled={isSubmitting}
        error={errors?.company}
        isRequired
        label={translate('forms:labels.company')}
        name={'company'}
        placeholder={translate('forms:placeholders.company')}
        {...register('company', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.name}
        isRequired
        label={translate('forms:labels.name')}
        name={'name'}
        placeholder={translate('forms:placeholders.name')}
        {...register('name', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.position}
        label={translate('forms:labels.position')}
        name={'position'}
        placeholder={translate('forms:placeholders.position')}
        {...register('position')}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.email}
        isRequired
        label={translate('forms:labels.email')}
        name={'email'}
        placeholder={translate('forms:placeholders.email')}
        {...register('email', {
          pattern: {
            message: translate('forms:rules.email'),
            value: regexes.email
          },
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.phone}
        label={translate('forms:labels.phone')}
        name={'phone'}
        placeholder={translate('forms:placeholders.phone')}
        {...register('phone')}
      />

      <Select
        disabled={isSubmitting}
        error={errors?.country}
        isRequired
        label={translate('forms:labels.country')}
        name={'country'}
        {...register('country', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      >
        <>
          <option value={''}>{translate('forms:placeholders.select')}</option>

          {map(countriesList, ({ alpha2, name }) => (
            <option
              key={alpha2}
              value={name}
            >
              {name}
            </option>
          ))}
        </>
      </Select>

      <Select
        disabled={isSubmitting}
        error={errors?.subject}
        isRequired
        label={translate('forms:labels.subject')}
        name={'subject'}
        {...register('subject', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      >
        <>
          <option value={''}>{translate('forms:placeholders.select')}</option>

          {Object.keys(subjectValues).map((name, index) => (
            <option
              key={index + 1}
              value={subjectValues[name]}
            >
              {translate(`forms:subjects.${name}`)}
            </option>
          ))}
        </>
      </Select>

      {isPartnership && (
        <>
          <Input
            disabled={isSubmitting}
            label={translate('forms:labels.gasType')}
            name={'gas_type'}
            placeholder={translate('forms:placeholders.gasType')}
            {...register('gas_type')}
          />

          <StyledCheckboxGroup
            disabled={isSubmitting || isLoadingTechnologies}
            label={translate('forms:labels.technologyType')}
            name={'technology_type'}
            options={technologiesOptions}
            {...register('technology_type')}
          />
        </>
      )}

      <Input
        as={'textarea'}
        disabled={isSubmitting}
        error={errors?.message}
        label={translate('forms:labels.message')}
        name={'message'}
        placeholder={translate('forms:placeholders.message')}
        {...register('message')}
      />

      <Checkbox
        disabled={isSubmitting}
        label={
          <TermsConditionsCheckboxLabel
            i18nKey={'forms:labels.termsConditions'}
          />
        }
        name={'acceptTerms'}
        {...register('acceptTerms', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      {errors.acceptTerms && (
        <HelpText hasError>
          {translate('forms:rules.acceptTermsConditions')}
        </HelpText>
      )}

      <StyledCheckbox
        disabled={isSubmitting}
        id={'subscribe_to_newsletter'}
        label={translate('forms:labels.allowMarketing')}
        name={'subscribe_to_newsletter'}
        {...register('subscribe_to_newsletter')}
      />

      <Button
        isLoading={isSubmitting}
        type={'submit'}
      >
        {translate('common:actions.submit')}
      </Button>

      <Transition
        in={isMessageVisible}
        mountOnEnter
        timeout={500}
        unmountOnExit
      >
        {state => (
          <MessageWrapper transitionState={state}>
            <Message
              appearance={messageAppearance}
              hasIcon={false}
              onClose={() => setIsMessageVisible(false)}
            >
              {translate(`contact-us:form.messages.${messageAppearance}`)}
            </Message>
          </MessageWrapper>
        )}
      </Transition>
    </Form>
  );
};

/**
 * Export `ContactForm` component.
 */

export default ContactForm;
